import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { SvgIcon } from "../../components/SvgComponents";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";


interface CinematicEpicInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const CinematicEpicInsights: React.FC<CinematicEpicInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // 1. Identify which bullet is active
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // 2. Entrance & Exit spring animations for bullet content
  const contentScale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.90,
    to: 1.0,
    config: { damping: 13, mass: 0.5, stiffness: 120 },
  });

  const contentOpacity = interpolate(elapsedFrames, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const contentTranslateY = interpolate(elapsedFrames, [0, 10], [15, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // 3. Central SVG breathing/floating animation
  const floatY = Math.sin(globalFrame * 0.04) * 10;
  const scalePulse = 1.0 + Math.sin(globalFrame * 0.03) * 0.03;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        flex: 1,
        boxSizing: "border-box",
        position: "relative",
        zIndex: 5,
        paddingBottom: "180px", // leave space for captions at the bottom
      }}
    >
      {/* 1. Large Glowing SVG Container (Floating) */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          flex: 1,
          maxHeight: "45%",
          transform: `scale(${contentScale * scalePulse}) translateY(${floatY}px)`,
          opacity: contentOpacity,
          filter: "drop-shadow(0 20px 45px rgba(255, 140, 0, 0.35))",
          zIndex: 3,
        }}
      >
        <div
          style={{
            background: "radial-gradient(circle, rgba(255, 140, 0, 0.08) 0%, transparent 70%)",
            padding: "20px",
            borderRadius: "50%",
          }}
        >
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#ff8c00"
                accentColor="#ff8c00"
                size={240}
                style="glow"
              />
            ) : (
              <SvgIcon
                name={activeBullet.icon || "sparkles"}
                width={340}
                height={340}
                primaryColor="#ff8c00"
              />
            );
          })()}
        </div>
      </div>

      {/* 2. Text Content Block inside a Film-Strip Box */}
      <div
        style={{
          width: "100%",
          padding: "0 35px",
          boxSizing: "border-box",
          transform: `scale(${contentScale}) translateY(${contentTranslateY}px)`,
          opacity: contentOpacity,
          zIndex: 4,
        }}
      >
        <div
          style={{
            width: "100%",
            background: "rgba(10, 10, 15, 0.94)",
            border: "1px solid rgba(255, 140, 0, 0.25)",
            borderRadius: "6px",
            padding: "36px",
            boxShadow: "0 20px 50px rgba(0, 0, 0, 0.6)",
            boxSizing: "border-box",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {/* Corner Cinematic Brackets */}
          <div style={{ position: "absolute", top: "10px", left: "10px", width: "12px", height: "12px", borderTop: "2px solid #ff8c00", borderLeft: "2px solid #ff8c00" }} />
          <div style={{ position: "absolute", top: "10px", right: "10px", width: "12px", height: "12px", borderTop: "2px solid #ff8c00", borderRight: "2px solid #ff8c00" }} />
          <div style={{ position: "absolute", bottom: "10px", left: "10px", width: "12px", height: "12px", borderBottom: "2px solid #ff8c00", borderLeft: "2px solid #ff8c00" }} />
          <div style={{ position: "absolute", bottom: "10px", right: "10px", width: "12px", height: "12px", borderBottom: "2px solid #ff8c00", borderRight: "2px solid #ff8c00" }} />

          {/* Documentary Node / Index Badge */}
          <div
            style={{
              fontFamily: "Space Grotesk, sans-serif",
              fontWeight: 800,
              fontSize: "12px",
              color: "rgba(255, 140, 0, 0.8)",
              letterSpacing: "4px",
              textTransform: "uppercase",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>[ TAKEAWAY 0{activeIndex + 1} / 0{bullets.length} ]</span>
            <div style={{ display: "flex", gap: "5px" }}>
              {bullets.map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: idx === activeIndex ? "#ff8c00" : "rgba(255, 255, 255, 0.2)",
                    boxShadow: idx === activeIndex ? "0 0 8px #ff8c00" : "none",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Main Bullet Text */}
          <p
            style={{
              fontFamily: "Outfit, sans-serif",
              fontSize: "44px",
              fontWeight: 800,
              lineHeight: 1.3,
              color: "#ffffff",
              textAlign: "left",
              letterSpacing: "-0.5px",
              margin: 0,
            }}
          >
            <HighlightText
              text={activeBullet.text}
              highlights={activeBullet.highlights}
              highlightStyle={{
                fontWeight: 900,
                color: "#ffb700", // warm amber highlight
                textShadow: "0 0 15px rgba(255, 183, 0, 0.35)",
                display: "inline",
              }}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
